import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, isStaffUser, getPortalType, hasPermission, hasAnyPermission } from '@/context/AuthContext';
import LoadingScreen from '@/components/layout/LoadingScreen';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import SANoAccessPage from '@/pages/SANoAccessPage';

// Ordered list: first match wins for redirect
const SA_PERMISSION_ROUTES = [
  { key: 'sa_overview',        path: null },               // null = stay on index (scoped dashboard)
  { key: 'sa_leads',           path: '/super-admin/leads' },
  { key: 'sa_companies',       path: '/super-admin/companies' },
  { key: 'sa_earnings',        path: '/super-admin/earnings' },
  { key: 'sa_plans',           path: '/super-admin/plans' },
  { key: 'sa_trials',          path: '/super-admin/trials' },
  { key: 'sa_notifications',   path: '/super-admin/notifications' },
  { key: 'sa_support',         path: '/super-admin/support' },
  { key: 'sa_prospects',       path: '/super-admin/prospects' },
  { key: 'sa_email_templates', path: '/super-admin/email-templates' },
  { key: 'sa_health',          path: '/super-admin/health' },
];

/** Renders the correct index content for /super-admin based on role & permissions. */
export function SAIndexRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

  // True super_admin: always show the full dashboard
  if (user.role === 'super_admin') return <SuperAdminDashboard />;

  // echohire_staff: decide based on saPermissions
  const perms = Array.isArray(user.saPermissions) ? user.saPermissions : [];

  // No permissions at all
  if (perms.length === 0) return <SANoAccessPage />;

  // Has sa_overview → show scoped dashboard
  if (perms.includes('sa_overview')) return <SuperAdminDashboard />;

  // No sa_overview → redirect to first permitted section
  for (const { key, path } of SA_PERMISSION_ROUTES) {
    if (key === 'sa_overview') continue;
    if (perms.includes(key) && path) return <Navigate to={path} replace />;
  }

  // Fallback: no recognisable permission
  return <SANoAccessPage />;
}

export function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

export function GuestOnly({ children }) {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

export function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!isStaffUser(user)) return <Navigate to="/" replace />;
  return children;
}

export function RequireCandidate({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (getPortalType(user) !== 'candidate') return <Navigate to="/" replace />;
  return children;
}

export function RequireSuperAdmin({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (user.role !== 'super_admin' && user.role !== 'echohire_staff') return <Navigate to="/" replace />;
  return children;
}

/**
 * RequirePermission — restricts a route to users with the given permission(s).
 * Usage: <RequirePermission permission="manage_masters" />
 *        <RequirePermission anyOf={['view_resumes','manage_resumes']} />
 */
export function RequirePermission({ permission, anyOf, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const required = anyOf || (permission ? [permission] : []);

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  const allowed = anyOf
    ? hasAnyPermission(user, required)
    : required.every(p => hasPermission(user, p));
  if (!allowed) {
    // Send to dashboard if allowed, otherwise settings (always open to staff)
    const fallback = hasPermission(user, 'view_dashboard') ? '/admin/dashboard' : '/admin/settings';
    return <Navigate to={fallback} replace />;
  }
  return children;
}

/**
 * RequireRole — restricts a route to specific role(s).
 * Redirects to /admin/dashboard if the user's role doesn't match.
 * Usage: <RequireRole role="admin"> or <RequireRole role={['admin','super_admin']}>
 */
export function RequireRole({ role, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const allowed = Array.isArray(role) ? role : [role];

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!allowed.includes(user.role)) return <Navigate to="/admin/dashboard" replace />;
  return children;
}

export function RootRedirect() {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (getPortalType(user) === 'candidate') return <Navigate to="/dashboard" replace />;
  if (user.role === 'super_admin' || user.role === 'echohire_staff') return <Navigate to="/super-admin" replace />;
  return <Navigate to="/admin/dashboard" replace />;
}
